/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Edit, Mail, Phone, Building2, Shield, Loader2, Save, Check, Star, Send, Award, X, UserPlus, Search, Upload, FileText, Trash2, Eye, Download, Printer, FileCheck, ScrollText, ShieldCheck, Calendar, Hash, MapPin, Globe, Users, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { formatCodeNAF } from '../../utils/codeNaf';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  cooper: { label: "Cooper's", color: "bg-amber-100 text-amber-700 border-amber-300" },
  admin: { label: "Admin", color: "bg-blue-100 text-blue-700 border-blue-300" },
  super_admin: { label: "Super Admin", color: "bg-purple-100 text-purple-700 border-purple-300" },
  expert: { label: "Expert", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
};

export const AssignCreateurModal = ({ membre, open, onClose, onSave, token }) => {
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setSearch('');
    setFilterRole('all');
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/admin/utilisateurs-assignables`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUtilisateurs(res.data.utilisateurs || []);
      } catch {
        toast.error("Erreur lors du chargement des utilisateurs");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [open, token]);

  const filtered = utilisateurs.filter(u => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return u.nom.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  const handleAssign = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/api/admin/membres/${membre.id}/assigner-createur`,
        { creator_id: selected.id, creator_role: selected.role, creator_name: selected.nom },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Creation assignee a ${selected.nom}`);
      onSave(res.data.membre || { ...membre, created_by: selected.id, created_by_name: selected.nom, created_by_role: selected.role });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'assignation");
    } finally {
      setSaving(false);
    }
  };

  if (!membre) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="assign-createur-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-indigo-600" />
            Assigner la creation du membre
          </DialogTitle>
          <DialogDescription>
            Assigner {membre.entreprise || `${membre.prenom} ${membre.nom}`} a un Cooper&apos;s, Admin ou Expert
          </DialogDescription>
        </DialogHeader>

        {/* Créateur actuel */}
        <div className="p-3 rounded-lg bg-gray-50 border text-sm" data-testid="current-creator-info">
          <span className="text-gray-500">Createur actuel : </span>
          <span className="font-medium">{membre.created_by_name || 'Systeme'}</span>
          {membre.created_by_role && (
            <span className={cn("ml-2 text-[10px] px-1.5 py-0.5 rounded-full border", ROLE_CONFIG[membre.created_by_role]?.color || "bg-gray-100 text-gray-600")}>
              {ROLE_CONFIG[membre.created_by_role]?.label || membre.created_by_role}
            </span>
          )}
        </div>

        {/* Filtres */}
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou email..."
              className="pl-9 h-9 text-sm" data-testid="assign-search-input"
            />
          </div>
          <div className="flex gap-1">
            {[{ key: 'all', label: 'Tous' }, { key: 'cooper', label: "Cooper's" }, { key: 'admin', label: 'Admin' }, { key: 'expert', label: 'Expert' }].map(f => (
              <Button key={f.key} variant={filterRole === f.key ? 'default' : 'outline'} size="sm"
                className={cn("h-8 text-xs", filterRole === f.key && "bg-indigo-600")}
                onClick={() => setFilterRole(f.key)} data-testid={`filter-role-${f.key}`}>
                {f.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Liste */}
        <div className="max-h-[280px] overflow-y-auto border rounded-lg divide-y" data-testid="assignable-users-list">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400">Aucun utilisateur trouve</div>
          ) : (
            filtered.map(u => (
              <button key={u.id} onClick={() => setSelected(u)}
                className={cn("w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-indigo-50 transition-colors",
                  selected?.id === u.id && "bg-indigo-50 ring-1 ring-inset ring-indigo-300")}
                data-testid={`user-option-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{u.nom}</p>
                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                </div>
                <span className={cn("text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap", ROLE_CONFIG[u.role]?.color || "bg-gray-100 text-gray-600")}>
                  {ROLE_CONFIG[u.role]?.label || u.role}
                </span>
                {selected?.id === u.id && <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />}
              </button>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={handleAssign} disabled={saving || !selected} className="bg-indigo-600 hover:bg-indigo-700" data-testid="assign-confirm-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Assigner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== UPLOAD DOCUMENT MODAL ====================

// Liste des types de documents disponibles, organisée par catégories.
// Les valeurs (`value`) sont les types stockés en DB ; les labels sont affichés
// à l'utilisateur. Le backend (`routes/ged_inline.py`) accepte tout type custom
// (validation laxiste, ligne 146).
